import { ClusterPinComponent } from '@/components/GoogleMapComponents/ClusterPinComponent'
import CurrentLocationComponent from '@/components/GoogleMapComponents/CurrentLocationComponent'
import ListPinComponent from '@/components/GoogleMapComponents/ListPinComponent'
import { googleMapOption } from '@/components/GoogleMapComponents/googleMapOption'
import mapController from '@/components/GoogleMapComponents/mapController'
import { mapOptionsCreator } from '@/components/GoogleMapComponents/mapOptionsCreator'
// import { spotsToMarkersThenAddBounds } from '@/components/GoogleMapComponents/listMakers'
import { currentPosition, spotIdToSliceIndex, watchId } from '@/features/spotMapSlice'
import type { RootState } from '@/store/store'
import styles from '@/styles/GoogleMap.module.scss'
import GoogleMapReact from 'google-map-react'
// @ts-ignore
import supercluster from 'points-cluster'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { defaultClusterRadius } from './GoogleMapComponents/consts'
import type { Bounds, ClusterMarkerProps, LatLng, MarkerProps } from './GoogleMapComponents/types'

type GoogleMapType = {
  spotInSheet: Api.SpotInSheetObject[]
  hasCoupon: boolean
  center: { latitude: number; longitude: number }
  /**
   * 通常は0.25、shokyoto2025の場合のみ10を指定している
   * */
  threshold: number
  /**
   * デフォルトのズームレベル
   * 通常は13、shokyoto2025の場合のみ5を指定している
   * */
  defaultZoom: number
  /**
   * 通常は12、shokyoto2025の場合も12を指定している
   * */
  maxZoom: number
  campaign: Api.CampaignResBody
}

/**
 * google-map-apiから提供されている、`bounds`を擬似的に再現した関数
 * thresholdの値は特に意図していないが`zoom=14`の時くらいに画面に入るくらいの大きさとしている
 * center: 画面中央の緯度経度
 * */
const pseudoBounds = ({ center, threshold }: { center: LatLng; threshold: number }): Bounds => {
  return {
    ne: {
      lat: center.lat + threshold,
      lng: center.lng + threshold,
    },
    nw: {
      lat: center.lat + threshold,
      lng: center.lng - threshold,
    },
    se: {
      lat: center.lat - threshold,
      lng: center.lng + threshold,
    },
    sw: {
      lat: center.lat - threshold,
      lng: center.lng - threshold,
    },
  }
}

const GoogleMap = ({ spotInSheet, hasCoupon, center, threshold, defaultZoom, maxZoom, campaign }: GoogleMapType) => {
  const { t } = useTranslation()
  const googleMapSize = useRef<HTMLHeadingElement>(null)
  const googleMapRef = useRef<HTMLHeadingElement>(null)
  const dispatch = useDispatch()
  const [googleMapArrayState, setGoogleMapArrayState] = useState(false)
  const [currentState, setCurrentStateState] = useState([])
  const [currentCenter, setCurrentCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [ReturnState, setReturnState] = useState('')
  const [locationElementState, setLocationElementState] = useState({
    locationSpan: null,
    locationButton: null,
  })
  const [MapState, setMapState] = useState({
    map: null,
    maps: null,
  })
  const [GeoLocationState, setGeoLocationState] = useState<Api.GeolocationPosition>()

  const [watchStatus, setWatchStatus] = useState({
    watchId: null,
  })
  const [MarkerListState, setMarkerListState] = useState<JSX.Element[]>()
  const [forceRerender, setForceRerender] = useState(0)

  const spotWatchIdStateSelector = useSelector((state: RootState) => state.spotMap.watchId)
  const spotIdToSliceIndexSelector = useSelector((state: RootState) => state.spotMap.spotIdToSliceIndex)

  useEffect(() => {
    navigator.geolocation.clearWatch(spotWatchIdStateSelector)
    navigator.geolocation.clearWatch(watchStatus.watchId)
    dispatch(watchId(watchStatus.watchId))
  }, [])

  useEffect(() => {
    if (GeoLocationState !== undefined) {
      onWatchSuccessCallback(GeoLocationState)
    }

    return
  }, [GeoLocationState])

  useEffect(() => {
    const { map, maps } = MapState
    const { locationSpan, locationButton } = locationElementState

    if (ReturnState === 'return') {
      navigator.geolocation.clearWatch(watchStatus.watchId)
      dispatch(watchId(watchStatus.watchId))
      dispatch(currentPosition(false))
      setReturnState('')
      map.setCenter(currentCenter)
      map.setZoom(map.zoom)
      // spotsToMarkersThenAddBounds({ map, maps, spotInSheet })
      locationSpan.textContent = t('spot.current_location', '現在地')
      locationButton.classList.remove('return')
    }
  }, [watchStatus, ReturnState])

  useEffect(() => {
    navigator.geolocation.clearWatch(watchStatus.watchId)
    dispatch(watchId(watchStatus.watchId))
    dispatch(currentPosition(false))
    setGoogleMapArrayState(false)

    const { map } = MapState
    const pos: LatLng = map ? JSON.parse(JSON.stringify(map.center)) : { lat: center.latitude, lng: center.longitude }
    const zoom = map ? map.zoom : defaultZoom

    const spotIdToSwiperIndexList: { spotId: number; sliceIndex: number }[] = []
    spotInSheet.map((v, index) => {
      spotIdToSwiperIndexList.push({ spotId: v.id, sliceIndex: index + 1 })
    })
    dispatch(spotIdToSliceIndex(spotIdToSwiperIndexList))
    const clustersAndPins = spotsToClustersAndPins({
      spots: spotInSheet,
      center: pos,
      bounds: pseudoBounds({ center: pos, threshold }),
      zoom,
      spotIdToSliceIndex: spotIdToSwiperIndexList,
    })
    if (clustersAndPins.length > 0) {
      setMarkerListState(clustersAndPins)
    }
    setGoogleMapArrayState(true)

    return
  }, [spotInSheet])

  function onWatchSuccessCallback(GeoLocationState: {
    latitude: number
    longitude: number
    accuracy?: number
  }) {
    const { locationSpan, locationButton } = locationElementState
    const { latitude, longitude, accuracy } = GeoLocationState
    const Current = []

    try {
      const pos = {
        lat: latitude,
        lng: longitude,
      }

      Current.push(
        <CurrentLocationComponent
          key={1}
          // @ts-ignore
          lat={pos.lat}
          lng={pos.lng}
          accuracy={accuracy}
          styles={styles}
        />,
      )

      setCurrentStateState(Current)
      dispatch(currentPosition(false))
      locationSpan.textContent = t('spot.back', 'もどる')
      locationButton.classList.add('return')
    } catch (error) {
      alert(t('spot.access_error_geo', '位置情報が取得できませんでした。'))
    }
  }

  function onWatchSuccess(position: GeolocationPosition) {
    const { latitude, longitude, accuracy } = position.coords

    try {
      if (accuracy <= 80) {
        setGeoLocationState({
          latitude: latitude,
          longitude: longitude,
          accuracy: accuracy,
        })
      }
    } catch (error) {
      console.log({ error })
      // alert('位置情報が取得できませんでした。')
    }
  }

  function currentLocationControl(
    currentLocationElement: HTMLDivElement,
    map: {
      center: LatLng
      zoom: number
      setCenter: (arg0: { lat: number; lng: number }) => void
      setZoom: (arg0: number) => void
    },
    maps: {
      event: {
        addDomListener: (arg0: HTMLButtonElement, arg1: string, arg2: () => void, arg3: boolean) => void
      }
    },
  ) {
    const locationButton = document.createElement('button')
    const locationSpan = document.createElement('span')
    const locationImg = document.createElement('img')
    locationImg.setAttribute('src', `${process.env.NEXT_PUBLIC_SITEURL}/images/img_geo.png`)
    locationButton.setAttribute('type', 'button')
    locationButton.classList.add(`${styles.current_location_button}`)
    locationImg.classList.add(`${styles.img_geo}`)
    locationSpan.classList.add('current_location_text')
    locationSpan.textContent = t('spot.current_location', '現在地')

    if (hasCoupon) {
      currentLocationElement.style.marginTop = '3rem'
    } else {
      currentLocationElement.style.marginTop = '0.5rem'
    }
    currentLocationElement.style.marginLeft = '0.5rem'
    locationButton.appendChild(locationSpan)
    locationButton.appendChild(locationImg)
    currentLocationElement.appendChild(locationButton)

    setMapState({ map, maps })
    setLocationElementState({ locationSpan, locationButton })

    maps.event.addDomListener(
      locationButton,
      'click',
      () => {
        dispatch(currentPosition(true))

        if (navigator.geolocation) {
          if (locationButton.classList.contains('return')) {
            setCurrentStateState([])
            setReturnState('return')
            return
          }

          // 初回だけ位置情報を取得し、現在地を中心としたmapを作る
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords
              try {
                setCurrentCenter(map.center)
                const pos = {
                  lat: latitude,
                  lng: longitude,
                }
                map.setCenter(pos)
                map.setZoom(map.zoom)
              } catch (error) {
                console.log({ error })
              }
            },
            () => {
              setCurrentStateState([])
              setReturnState('return')
            },
            googleMapOption,
          )

          // 追跡機能 初回以降はwatchPositionから位置情報を収集し続ける
          const watch_id = navigator.geolocation.watchPosition(
            onWatchSuccess,
            (error) => {
              const errorMessage = {
                0: t('spot.access_error_message_01', '原因不明のエラーが発生しました。'),
                1: t('spot.access_error_message_02', '位置情報の使用許可がおりませんでした。'),
                2: t('spot.access_error_message_03', '位置情報が取得できませんでした。'),
                3: t(
                  'spot.access_error_message_04',
                  '位置情報が取得できませんでした。しばらく経ってからアクセスするか、少し場所を移動して再試行してください。',
                ),
              }

              setCurrentStateState([])
              setReturnState('return')
              alert(errorMessage[error.code])
            },
            googleMapOption,
          )

          dispatch(watchId(watch_id))
          setWatchStatus({ watchId: watch_id })
        } else {
          alert(t('spot.access_error_geo', '位置情報が取得できませんでした。'))
        }
      },
      false,
    )
  }

  const handleApiLoaded = ({ map, maps }) => {
    const mapControllerElement = document.createElement('div')
    const currentLocationElement = document.createElement('div')

    mapController(mapControllerElement, map, maps, styles)
    currentLocationControl(currentLocationElement, map, maps)

    // デフォルトを全域が見えつつ中心に合わせたい場合には、これを有効化する
    // spotsToMarkersThenAddBounds({ map, maps, spotInSheet })

    map.controls[maps.ControlPosition.RIGHT_CENTER].push(mapControllerElement)
    map.controls[maps.ControlPosition.LEFT_TOP].push(currentLocationElement)

    const pos = JSON.parse(JSON.stringify(map.center))
    setCurrentCenter(pos)
  }

  const createClusters = ({
    markers,
    center,
    bounds,
    zoom,
  }: {
    markers: MarkerProps[]
    center: LatLng
    bounds: Bounds
    zoom: number
  }): ClusterMarkerProps[] => {
    const getClusters = (markers: MarkerProps[], center: LatLng, bounds: Bounds, zoom: number) => {
      const options = {
        center,
        zoom,
        bounds,
      }
      // defaultZoom - 1までクラスタを形成する。
      // この値は目視で決めているので、何かしらの指標があったわけではない。
      // クラスタ化の時のみ、以前と同じようにid順にクラスタ化するようにする
      const clusters = supercluster(
        markers.sort((a, b) => (a.id > b.id ? 1 : -1)),
        {
          minZoom: 0,
          maxZoom,
          radius: defaultClusterRadius,
        },
      )

      return clusters(options)
    }

    return getClusters(markers, center, bounds, zoom).map(
      ({
        wx,
        wy,
        points,
      }: {
        wx: number
        wy: number
        points: MarkerProps[]
      }) =>
        ({
          lat: wy,
          lng: wx,
          id: `cluster_${points.length}_${points[0].id}`,
          markers: points,
          key: `cluster_${points.length}_${points[0].id}`,
        }) as ClusterMarkerProps,
    )
  }

  const spotsToClustersAndPins = ({
    spots,
    center,
    bounds,
    zoom,
    spotIdToSliceIndex,
  }: {
    spots: Api.SpotInSheetObject[]
    center: LatLng
    bounds: Bounds
    zoom: number
    spotIdToSliceIndex?: { spotId: number; sliceIndex: number }[]
  }): JSX.Element[] => {
    const markerProps: MarkerProps[] = []
    const markerList: JSX.Element[] = []
    spots.map((e) => {
      markerProps.push({
        id: e.id,
        name: e.name,
        address: e.address,
        stamp: e.stamp,
        image: e.image,
        lat: e.latitude,
        lng: e.longitude,
        displayNumber: e.displayNumber,
        crowded: e.crowded,
        campaignMapPin: campaign.map_pin,
        campaignType: campaign.type,
      })
    })

    const spotIdToSliceIndexUse = spotIdToSliceIndex ? spotIdToSliceIndex : spotIdToSliceIndexSelector
    const clusters = createClusters({ markers: markerProps, center, bounds, zoom })
    clusters.map((c, i) => {
      if (c.markers.length === 1) {
        const element = c.markers[0]
        const targetSpot = spotIdToSliceIndexUse.filter((s) => s.spotId === element.id)[0]
        if (targetSpot) {
          markerList.push(
            <ListPinComponent
              key={`${i}_${element.id}_${element.lat}_${element.lng}`}
              id={targetSpot.sliceIndex}
              name={element.name}
              address={element.address}
              stamp={element.stamp}
              image={element.image}
              lat={element.lat}
              lng={element.lng}
              displayNumber={element.displayNumber}
              crowded={element.crowded}
              campaignType={element.campaignType}
              campaignMapPin={element.campaignMapPin}
            />,
          )
        }
      } else {
        // クラスタ化に関する時のみ、以前と同じようにid順にクラスタ化するようにする
        const element = c.markers.sort((a, b) => (a.id > b.id ? 1 : -1))[0]
        markerList.push(<ClusterPinComponent markers={c.markers} lat={element.lat} lng={element.lng} key={c.key} />)
      }
    })
    return markerList
  }

  const onChange = (map: any) => {
    const pos = JSON.parse(JSON.stringify(map.center))
    const bounds = JSON.parse(JSON.stringify(map.bounds))
    const zoom = map.zoom
    if (bounds?.nw?.lat === 0 || bounds?.nw?.lng === 0) {
      // コンポーネントを強制的に再レンダリング
      // これは、GoogleMapReactのバグで、onChangeのハンドラが正しく登録されていない！！！！
      // そのため強制的にGoogleMapのコンポーネントを再レンダリングしている。
      setForceRerender((prev) => prev + 1)
      return
    }

    setMarkerListState(
      spotsToClustersAndPins({
        spots: spotInSheet,
        center: pos,
        bounds,
        zoom,
        spotIdToSliceIndex: spotIdToSliceIndexSelector,
      }),
    )
  }
  const [WindowHState, setWindowHState] = useState<number>()

  const onChangeBody = () => {
    // eslint-disable-next-line prefer-const
    let timeoutId: NodeJS.Timeout
    if (timeoutId) return
    setTimeout(() => {
      const H = document.getElementById('l_header').clientHeight
      setWindowHState(window.innerHeight - H - 80)
    }, 500)
  }

  useEffect(() => {
    window.addEventListener('resize', onChangeBody)

    return () => window.removeEventListener('resize', onChangeBody)
  }, [])

  useEffect(() => {
    if (googleMapArrayState === true) {
      const H = document.getElementById('l_header').clientHeight
      googleMapSize.current.style.height = WindowHState ? WindowHState + 'px' : window.innerHeight - H - 80 + 'px'
    }
  }, [googleMapArrayState, WindowHState])

  return useMemo(() => {
    return (
      <div ref={googleMapRef}>
        <div className={`w-full ${styles.c_google_map}`} ref={googleMapSize}>
          {googleMapArrayState && (
            <GoogleMapReact
              key={forceRerender}
              bootstrapURLKeys={{
                key: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY,
              }}
              yesIWantToUseGoogleMapApiInternals
              defaultZoom={defaultZoom}
              defaultCenter={{ lat: center.latitude, lng: center.longitude }}
              onGoogleApiLoaded={handleApiLoaded}
              options={mapOptionsCreator}
              onChange={onChange}
            >
              {MarkerListState && MarkerListState}
              {currentState && currentState}
            </GoogleMapReact>
          )}
        </div>
      </div>
    )
  }, [MarkerListState, currentState, googleMapArrayState, forceRerender])
}

export default GoogleMap
